/** Error codes the API returns (backend/src/core/http/errors.ts) plus client-side ones. */
export const API_ERROR_CODES = [
  'VALIDATION_FAILED',
  'NOT_FOUND',
  'CONFLICT',
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'INVALID_CREDENTIALS',
  'PASSWORD_NOT_SET',
  'OTP_INVALID',
  'TOKEN_INVALID',
  'SCHOOL_REQUIRED',
  'SCHOOL_SUSPENDED',
  'SCHOOL_READ_ONLY',
  'FEATURE_DISABLED',
  'FEATURE_NEEDS',
  'FEATURE_HAS_DEPENDENTS',
  'FEATURE_NOT_YOURS',
  'PAYROLL_HAS_OPEN_ADVANCES',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'SELF_APPROVAL',
  'SEPARATION_OF_DUTIES',
  'INVALID_STATE',
  'REFERENCE_REQUIRED',
  'CASH_ACCOUNT_MISMATCH',
  'CASH_DAY_CLOSED',
  'NOTE_REQUIRED',
  'DEDUCTION_CAP_EXCEEDED',
  'ADVANCE_TOO_LONG',
  'ADVANCE_IN_PAYROLL',
  'NO_CONTRACT',
  'PAYROLL_LOCKED',
  'NO_CURRENT_YEAR',
  'NOT_YOUR_CLASS',
  'SCHOOL_HOLIDAY',
  'ATTENDANCE_DATE_LOCKED',
  'SMS_NOT_AVAILABLE',
  'STUDENT_LOGIN_GRADE',
  'TEACHER_BUSY',
  'NO_GRADING_PLAN',
  'GRADING_PLAN_IN_USE',
  'GRADES_LOCKED',
  'OWNER_MUST_DECIDE',
  'ALREADY_PAID',
  'NO_STAFF_RECORD',
  'TOO_MANY_ATTEMPTS',
  'TOO_MANY_REQUESTS',
  'INTERNAL',
  // Client side
  'NETWORK',
  'ERROR',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export interface ValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }

  /** Field paths from a VALIDATION_FAILED answer, e.g. ["guardians.0.phone"]. */
  get issues(): ValidationIssue[] {
    const issues = this.details?.issues;
    return Array.isArray(issues) ? (issues as ValidationIssue[]) : [];
  }

  /** The request never reached the server (offline, DNS, CORS, aborted proxy). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

const KNOWN = new Set<string>(API_ERROR_CODES);

function codeForStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION_FAILED';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'TOO_MANY_REQUESTS';
    default:
      return status >= 500 ? 'INTERNAL' : 'ERROR';
  }
}

/** Maps an error response body ({ statusCode, code, message, details? }) to an ApiError. */
export function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const b = body as { code?: unknown; message?: unknown; details?: unknown };
    const code = typeof b.code === 'string' && KNOWN.has(b.code) ? (b.code as ApiErrorCode) : codeForStatus(status);
    const message = Array.isArray(b.message) ? b.message.join(', ') : typeof b.message === 'string' ? b.message : `HTTP ${status}`;
    const details = b.details && typeof b.details === 'object' ? (b.details as Record<string, unknown>) : undefined;
    return new ApiError(status, code, message, details);
  }
  return new ApiError(status, codeForStatus(status), typeof body === 'string' && body ? body.slice(0, 200) : `HTTP ${status}`);
}

export function networkError(cause: unknown): ApiError {
  return new ApiError(0, 'NETWORK', cause instanceof Error ? cause.message : 'Network error');
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
